import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function TimeCalc() {
  const [tab, setTab] = useState('addsub');
  return (
    <CalcShell look="cnet"
      title="Time Calculator"
      subtitle="Add, subtract, interval between times, project a date + time forward, or evaluate a full time expression."
      about={<>
        <p>Time is expressed in hours, minutes and seconds. This tool provides four modes: arithmetic add/subtract, interval between two times, date+time projection and a free-form expression evaluator that accepts strings like <code>2h 30m + 1h 45m - 15s</code>.</p>
      </>}
    >
      <div className="flex gap-2 mb-5 flex-wrap">
        {[{id:'addsub',l:'Add / Subtract'},{id:'between',l:'Time Between'},{id:'projection',l:'Date + Time'},{id:'expression',l:'Expression'}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid={`time-tab-${t.id}`}>{t.l}</button>
        ))}
      </div>
      {tab === 'addsub' && <AddSub />}
      {tab === 'between' && <Between />}
      {tab === 'projection' && <Projection />}
      {tab === 'expression' && <Expression />}
    </CalcShell>
  );
}

function AddSub() {
  const [h1, setH1] = useState(2); const [m1, setM1] = useState(30); const [s1, setS1] = useState(15);
  const [h2, setH2] = useState(1); const [m2, setM2] = useState(45); const [s2, setS2] = useState(30);
  const [op, setOp] = useState('+');
  const r = useMemo(() => {
    let t1 = h1*3600 + m1*60 + s1; let t2 = h2*3600 + m2*60 + s2;
    let sec = op === '+' ? t1 + t2 : t1 - t2;
    const sign = sec < 0 ? '-' : ''; sec = Math.abs(sec);
    const H = Math.floor(sec/3600); sec %= 3600;
    const M = Math.floor(sec/60); const S = sec % 60;
    return { H, M, S, sign };
  }, [h1, m1, s1, h2, m2, s2, op]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="grid grid-cols-3 gap-3">
          <div className="field"><label>H</label><input type="number" value={h1} onChange={e => setH1(+e.target.value || 0)} /></div>
          <div className="field"><label>M</label><input type="number" value={m1} onChange={e => setM1(+e.target.value || 0)} /></div>
          <div className="field"><label>S</label><input type="number" value={s1} onChange={e => setS1(+e.target.value || 0)} /></div>
        </div>
        <div className="field"><label>Operation</label><select value={op} onChange={e => setOp(e.target.value)}><option>+</option><option>-</option></select></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="field"><label>H</label><input type="number" value={h2} onChange={e => setH2(+e.target.value || 0)} /></div>
          <div className="field"><label>M</label><input type="number" value={m2} onChange={e => setM2(+e.target.value || 0)} /></div>
          <div className="field"><label>S</label><input type="number" value={s2} onChange={e => setS2(+e.target.value || 0)} /></div>
        </div>
      </div>
      <div>
        <div className="result-panel"><div className="result-title">Result</div><div className="result-value">{r.sign}{r.H}h {r.M}m {r.S}s</div></div>
      </div>
    </div>
  );
}

function Between() {
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:30');
  const r = useMemo(() => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let mins = (eh*60 + em) - (sh*60 + sm);
    if (mins < 0) mins += 24*60;
    const H = Math.floor(mins/60); const M = mins % 60;
    return { H, M, decimal: mins/60 };
  }, [start, end]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="field"><label>Start Time</label><input type="time" value={start} onChange={e => setStart(e.target.value)} /></div>
        <div className="field"><label>End Time</label><input type="time" value={end} onChange={e => setEnd(e.target.value)} /></div>
      </div>
      <div>
        <div className="result-panel mb-4"><div className="result-title">Duration</div><div className="result-value">{r.H}h {r.M}m</div></div>
        <table className="data-table"><tbody><tr><td>Decimal hours</td><td className="text-right">{r.decimal.toFixed(2)}</td></tr></tbody></table>
      </div>
    </div>
  );
}

function Projection() {
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 16));
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(2);
  const [mins, setMins] = useState(30);
  const [op, setOp] = useState('+');
  const result = useMemo(() => {
    const d = new Date(start);
    const totalMs = (days*86400 + hours*3600 + mins*60) * 1000;
    return new Date(d.getTime() + (op === '+' ? totalMs : -totalMs));
  }, [start, days, hours, mins, op]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="field"><label>Start Date &amp; Time</label><input type="datetime-local" value={start} onChange={e => setStart(e.target.value)} /></div>
        <div className="field"><label>Operation</label><select value={op} onChange={e => setOp(e.target.value)}><option>+</option><option>-</option></select></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="field"><label>Days</label><input type="number" value={days} onChange={e => setDays(+e.target.value || 0)} /></div>
          <div className="field"><label>Hours</label><input type="number" value={hours} onChange={e => setHours(+e.target.value || 0)} /></div>
          <div className="field"><label>Minutes</label><input type="number" value={mins} onChange={e => setMins(+e.target.value || 0)} /></div>
        </div>
      </div>
      <div>
        <div className="result-panel"><div className="result-title">Result Date &amp; Time</div><div className="result-value text-2xl">{result.toLocaleString(undefined, { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div></div>
      </div>
    </div>
  );
}

// Expression evaluator. Accepts tokens like "2d 3h 15m 30s" combined with + and -.
function parseTimeToken(tok) {
  const re = /(-?\d+(?:\.\d+)?)\s*(d|h|m|s|days?|hours?|mins?|minutes?|secs?|seconds?)?/gi;
  let sec = 0, match, matched = false;
  while ((match = re.exec(tok)) !== null) {
    matched = true;
    const v = parseFloat(match[1]);
    const u = (match[2] || 's').toLowerCase();
    if (u.startsWith('d')) sec += v * 86400;
    else if (u.startsWith('h')) sec += v * 3600;
    else if (u.startsWith('mi') || u === 'm' || u.startsWith('m')) sec += v * 60;
    else sec += v;
  }
  return matched ? sec : NaN;
}
function evalExpression(expr) {
  // split on + and - operators, keeping sign
  const tokens = expr.replace(/\s+/g,' ').match(/[+-]?\s*[^+\-]+/g);
  if (!tokens) return NaN;
  let total = 0;
  for (const raw of tokens) {
    const t = raw.trim();
    if (!t) continue;
    const sign = t.startsWith('-') ? -1 : 1;
    const body = t.replace(/^[+-]\s*/, '');
    const v = parseTimeToken(body);
    if (isNaN(v)) return NaN;
    total += sign * v;
  }
  return total;
}

function Expression() {
  const [expr, setExpr] = useState('2h 30m 15s + 1h 45m 30s - 15m');
  const sec = useMemo(() => evalExpression(expr), [expr]);
  const invalid = isNaN(sec);
  const sign = sec < 0 ? '-' : ''; const abs = Math.abs(sec);
  const D = Math.floor(abs / 86400);
  const H = Math.floor((abs % 86400) / 3600);
  const M = Math.floor((abs % 3600) / 60);
  const S = +(abs % 60).toFixed(3);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="field"><label>Time Expression</label>
          <input value={expr} onChange={e => setExpr(e.target.value)} className="font-mono" data-testid="time-expression" placeholder="e.g. 2h 30m + 1h 45m 30s - 15s"/>
        </div>
        <p className="text-xs text-slate-500">Supported units: <b>d</b>, <b>h</b>, <b>m</b>, <b>s</b> (or their long names). Combine tokens with <code>+</code> / <code>-</code>.</p>
      </div>
      <div>
        {invalid ? (
          <div className="text-rose-600 text-sm">Could not parse. Example: <code>2h 30m + 45s</code></div>
        ) : (
          <>
            <div className="result-panel mb-3"><div className="result-title">Total</div><div className="result-value">{sign}{D > 0 ? `${D}d ` : ''}{H}h {M}m {S}s</div></div>
            <table className="data-table"><tbody>
              <tr><td>Total seconds</td><td className="text-right font-mono">{sec.toFixed(3)}</td></tr>
              <tr><td>Total minutes</td><td className="text-right font-mono">{(sec/60).toFixed(3)}</td></tr>
              <tr><td>Total hours</td><td className="text-right font-mono">{(sec/3600).toFixed(4)}</td></tr>
              <tr><td>Total days</td><td className="text-right font-mono">{(sec/86400).toFixed(5)}</td></tr>
            </tbody></table>
          </>
        )}
        <StepsPanel steps={[
          'Split expression on <b>+</b> / <b>-</b> operators keeping signs.',
          'For each token, parse pairs like <code>2h</code>, <code>30m</code>, <code>15s</code>, <code>1d</code>.',
          'Convert each to seconds and sum with sign.',
          'Present the total as days / hours / minutes / seconds.'
        ]} />
      </div>
    </div>
  );
}
